/**
 * Generates a RFC 4122 version 4 UUID.
 *
 * `crypto.randomUUID()` is only exposed in secure contexts (HTTPS or
 * localhost), so it is missing when the app is opened over a plain-http LAN
 * address such as http://192.168.1.10:3000. `crypto.getRandomValues()` is
 * available in insecure contexts as well, so we fall back to building the UUID
 * ourselves, and finally to `Math.random()` for environments without any
 * crypto at all.
 */
export function generateUUID(): string {
    if (typeof crypto !== "undefined") {
        if (typeof crypto.randomUUID === "function") {
            return crypto.randomUUID();
        }

        if (typeof crypto.getRandomValues === "function") {
            const bytes = crypto.getRandomValues(new Uint8Array(16));
            // Set the version (4) and variant (10xx) bits.
            bytes[6] = (bytes[6] & 0x0f) | 0x40;
            bytes[8] = (bytes[8] & 0x3f) | 0x80;

            const hex = Array.from(bytes, (byte) =>
                byte.toString(16).padStart(2, "0"),
            ).join("");

            return [
                hex.slice(0, 8),
                hex.slice(8, 12),
                hex.slice(12, 16),
                hex.slice(16, 20),
                hex.slice(20),
            ].join("-");
        }
    }

    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
        const random = (Math.random() * 16) | 0;
        const value = char === "x" ? random : (random & 0x3) | 0x8;
        return value.toString(16);
    });
}
